import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { SIDEBAR_ID } from './constants'
import ImpersonationBanner from './ImpersonationBanner'
import Nav from './Nav'
import Sidebar from './Sidebar'

import styles from './styles.module.scss'

import UpgradePrompt from '@/components/UpgradePrompt'
import { usePageViewTracking } from '@/hooks/usePageViewTracking'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { selectEmail } from '@/store/features/user/userSlice'
import { initConciergeWidget, teardownConciergeWidget } from '@/utils/conciergeWidget'

const INTERNAL_ROUTE_PREFIXES = [
  '/app/notifications',
  '/app/users',
  '/app/enterprises',
  '/app/analytics-portal',
  '/app/pilots',
  '/app/domain-management',
  '/app/billing-ops',
  '/app/flow-state',
]

// Exempt: the manage page is where "View as Admin" lives — the thunk needs
// time to fetch target enterprise data before navigating away.
const IMPERSONATION_EXEMPT_ROUTES = ['/app/enterprises/manage']

const MOBILE_QUERY = '(max-width: 768px)'
const SIDEBAR_COLLAPSED_KEY = 'app-sidebar-collapsed'

const subscribeMobile = (onChange: () => void) => {
  const query = window.matchMedia(MOBILE_QUERY)
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
const getIsMobile = () => window.matchMedia(MOBILE_QUERY).matches

const readCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'
  } catch {
    return false
  }
}

const AppLayout = () => {
  const location = useLocation()
  const isMobile = useSyncExternalStore(subscribeMobile, getIsMobile)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const sidebarVisible = isMobile ? drawerOpen : !collapsed
  const closeDrawer = useCallback(() => setDrawerOpen(false), [])
  const toggleSidebar = () => {
    if (isMobile) {
      setDrawerOpen((open) => !open)
      return
    }
    const next = !collapsed
    setCollapsed(next)
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next))
    } catch (error) {
      console.warn('Failed to persist sidebar state', error)
    }
  }
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const email = useSelector(selectEmail)
  usePageViewTracking()

  useEffect(() => {
    initConciergeWidget(email)
    return () => teardownConciergeWidget()
  }, [email])

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false)
  }, [isMobile])

  useEffect(() => {
    if (!isMobile || !drawerOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isMobile, drawerOpen])

  useEffect(() => {
    const drawerActive = isMobile && drawerOpen
    const content = contentRef.current
    content?.toggleAttribute('inert', drawerActive)
    if (!drawerActive) return

    const toggle = document.querySelector<HTMLElement>(`[aria-controls="${SIDEBAR_ID}"]`)
    document.getElementById(SIDEBAR_ID)?.querySelector<HTMLElement>('a[href], button')?.focus()

    return () => {
      content?.removeAttribute('inert')
      if (toggle?.isConnected) toggle.focus()
    }
  }, [isMobile, drawerOpen])

  // Redirect away from internal routes during impersonation
  const onInternalRoute = INTERNAL_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p))
  const isExempt = IMPERSONATION_EXEMPT_ROUTES.some((r) => location.pathname === r)
  if (impersonating && onInternalRoute && !isExempt) {
    return <Navigate to="/app/team" replace />
  }

  return (
    <div className={styles.root}>
      <Nav sidebarToggle={{ expanded: sidebarVisible, onToggle: toggleSidebar }} />
      <ImpersonationBanner />
      <div className={styles.container}>
        {isMobile && drawerOpen && <div className={styles.sidebarBackdrop} onClick={closeDrawer} aria-hidden="true" />}
        <Sidebar
          id={SIDEBAR_ID}
          className={isMobile ? `${styles.drawer} ${drawerOpen ? styles.drawerOpen : ''}` : undefined}
          hidden={!isMobile && collapsed}
          onNavigate={isMobile ? closeDrawer : undefined}
        />
        <div ref={contentRef} className={styles.content}>
          <Outlet />
        </div>
      </div>
      {!isMobile && !collapsed && <UpgradePrompt />}
    </div>
  )
}

export default AppLayout
